import { useState } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Download, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContractGenerator = ({ user, onLogout }) => {
  const [formData, setFormData] = useState({
    contract_type: "",
    party_a: "",
    party_b: "",
    terms: "",
    additional_info: ""
  });
  const [contract, setContract] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/ai/generate-contract`, formData);
      setContract(response.data.contract);
      toast.success("تم إنشاء العقد بنجاح");
    } catch (error) {
      toast.error("خطأ في إنشاء العقد: " + (error.response?.data?.detail || "حاول مرة أخرى"));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const element = document.createElement("a");
    const file = new Blob([contract], { type: "text/plain;charset=utf-8" });
    element.href = URL.createObjectURL(file);
    element.download = `عقد_${formData.contract_type}_${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success("تم تحميل العقد");
  };

  const contractTypes = [
    { value: "عقد بيع", label: "عقد بيع" },
    { value: "عقد إيجار", label: "عقد إيجار" },
    { value: "عقد عمل", label: "عقد عمل" },
    { value: "عقد شراكة", label: "عقد شراكة" },
    { value: "عقد قرض", label: "عقد قرض" },
    { value: "عقد خدمات", label: "عقد خدمات" },
    { value: "عقد وكالة", label: "عقد وكالة" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8" data-testid="contract-generator-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">منشئ العقود الذكي</h1>
          <p className="text-lg text-slate-600">إنشاء عقود قانونية احترافية بضغطة زر</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                  <FileText className="w-6 h-6 text-amber-600" />
                  بيانات العقد
                </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="contract_type">نوع العقد *</Label>
                  <Input
                    id="contract_type"
                    data-testid="contract-type-input"
                    value={formData.contract_type}
                    onChange={(e) => setFormData({ ...formData, contract_type: e.target.value })}
                    placeholder="اكتب نوع العقد (مثال: عقد إيجار، عقد بيع، عقد عمل...)"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="party_a">الطرف الأول *</Label>
                  <Input
                    id="party_a"
                    placeholder="اسم الطرف الأول"
                    value={formData.party_a}
                    onChange={(e) => setFormData({ ...formData, party_a: e.target.value })}
                    required
                    data-testid="party-a-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="party_b">الطرف الثاني *</Label>
                  <Input
                    id="party_b"
                    placeholder="اسم الطرف الثاني"
                    value={formData.party_b}
                    onChange={(e) => setFormData({ ...formData, party_b: e.target.value })}
                    required
                    data-testid="party-b-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="terms">الشروط والبنود *</Label>
                  <Textarea
                    id="terms"
                    placeholder="اذكر الشروط والبنود الأساسية للعقد..."
                    rows={6}
                    value={formData.terms}
                    onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                    required
                    data-testid="terms-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="additional_info">معلومات إضافية</Label>
                  <Textarea
                    id="additional_info"
                    placeholder="أي تفاصيل إضافية تريد تضمينها..."
                    rows={3}
                    value={formData.additional_info}
                    onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
                    data-testid="additional-info-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full gap-2"
                  disabled={loading}
                  data-testid="generate-contract-button"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      جاري إنشاء العقد...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      إنشاء العقد
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Generated Contract */}
          <Card className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>العقد المنشأ</CardTitle>
                {contract && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownload}
                    className="gap-2"
                    data-testid="download-contract-button"
                  >
                    <Download className="w-4 h-4" />
                    تحميل
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto">
              {contract ? (
                <div className="p-4 bg-slate-50 rounded-lg" data-testid="generated-contract">
                  <div className="text-slate-800 whitespace-pre-wrap font-serif leading-relaxed">
                    {contract}
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">
                  <div className="text-center">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>لم يتم إنشاء عقد بعد</p>
                    <p className="text-sm">املأ النموذج واضغط "إنشاء العقد"</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContractGenerator;
